/**
 * Reading a sponsor's member file in the browser.
 *
 * 🔑 THE SERVER DECIDES; THIS ONLY SAVES A ROUND TRIP. radcustomer's
 * app/roster/roster.go ValidateBatch is the rule. Everything here mirrors it
 * so a sponsor sees "line 14: mobile" before uploading, but a row this accepts
 * can still be refused there -- the approved plans and the organisation's
 * speed and validity limits are known only to the server, which is what
 * "Check with server" (dry_run) is for.
 *
 * A row's plan (radius-wiki sponsor-roster-console-design.md §11): plan_id
 * alone; else speed_mbps with valid_days OR valid_until, each defaulted; else
 * 5 Mbps for 30 days. Category is a label.
 *
 * No CSV library: none is in package.json, and the quoting rules are small.
 */

export const MAX_ROWS_PER_BATCH = 5000;
export const DEFAULT_SPEED_MBPS = 5;
export const DEFAULT_VALID_DAYS = 30;
const MAX_NAME = 128;
const MAX_REF = 64;
const MAX_LABEL = 64;
const MAX_CATEGORY = 32;
const MAX_PLAN_ID = 64;
const MAX_NUMBER_LEN = 6;

export const REQUIRED_COLUMNS = ["mobile", "full_name", "category", "member_ref"] as const;
export const OPTIONAL_COLUMNS = ["group_label", "location_label", "plan_id", "speed_mbps", "valid_days", "valid_until"] as const;
const KNOWN = new Set<string>([...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS]);

/** One row as radcustomer's roster.Row takes it. The plan fields are the file's text. */
export interface RosterRow {
  line: number;
  mobile: string;
  full_name: string;
  category: string;
  member_ref: string;
  group_label?: string;
  location_label?: string;
  plan_id?: string;
  speed_mbps?: string;
  valid_days?: string;
  valid_until?: string;
}

/** radcustomer's roster.RowError. Line 1 is the header, line 0 the whole file. */
export interface RowProblem {
  line: number;
  field: string;
  message: string;
}

export interface CsvRecord {
  /** The physical line the record starts on, as a spreadsheet numbers it. */
  line: number;
  fields: string[];
}

/** RFC 4180: quoted fields, "" for a quote, CRLF or LF, a leading BOM. */
export function parseCsv(input: string): CsvRecord[] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const records: CsvRecord[] = [];
  let fields: string[] = [];
  let field = "";
  let quoted = false;
  let line = 1;
  let start = 1;
  let any = false;

  const endRecord = () => {
    fields.push(field);
    records.push({ line: start, fields });
    fields = [];
    field = "";
    any = false;
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        if (c === "\n") line++;
        field += c;
      }
      continue;
    }
    if (c === '"') {
      quoted = true;
      any = true;
    } else if (c === ",") {
      fields.push(field);
      field = "";
      any = true;
    } else if (c === "\r" || c === "\n") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      endRecord();
      line++;
      start = line;
    } else {
      field += c;
      any = true;
    }
  }
  if (any || field !== "" || fields.length > 0) endRecord();
  return records;
}

/**
 * The 10-digit Indian mobile, or null. Same steps as NormalizeMobile: digits
 * kept, spaces and hyphens dropped, "+" only as the first character, then a
 * leading 91 (12 digits) or 0 (11 digits) removed.
 */
export function normalizeMobile(s: string): string | null {
  const t = s.trim();
  let d = "";
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    if (c >= "0" && c <= "9") d += c;
    else if (c === " " || c === "-") continue;
    else if (c === "+" && i === 0) continue;
    else return null;
  }
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  if (d.length !== 10 || d[0]! < "6" || d[0]! > "9") return null;
  return d;
}

/** Today's date in India (fixed +05:30, no daylight saving) as YYYY-MM-DD. */
export function indiaToday(now: Date): string {
  return new Date(now.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/** A real calendar date in exactly YYYY-MM-DD, as Go's time.Parse("2006-01-02") takes. */
function isDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/** A positive whole number as a spreadsheet writes it, as roster.wholeNumber reads it. */
function wholeNumber(s: string): number | null {
  if (s === "" || s.length > MAX_NUMBER_LEN || !/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
}

/** Characters as Go's utf8.RuneCountInString counts them. */
const runes = (s: string) => [...s].length;

/**
 * What a row's plan will be, in words: the named plan, or the speed and
 * validity of the shared plan it gets (defaults filled in).
 */
export function planSummary(row: Pick<RosterRow, "plan_id" | "speed_mbps" | "valid_days" | "valid_until">): string {
  if (row.plan_id) return row.plan_id;
  const speed = row.speed_mbps || String(DEFAULT_SPEED_MBPS);
  if (row.valid_until) return `${speed} Mbps to ${row.valid_until}`;
  const days = row.valid_days || String(DEFAULT_VALID_DAYS);
  const defaulted = !row.speed_mbps && !row.valid_days ? " (default)" : "";
  return `${speed} Mbps, ${days} days${defaulted}`;
}

export interface RosterRead {
  rows: RosterRow[];
  problems: RowProblem[];
}

/**
 * The file's rows, or every problem in it. All or nothing, as the server
 * commits: rows is empty whenever problems is not.
 */
export function readRoster(text: string, today: string): RosterRead {
  const records = parseCsv(text);
  const header = records[0];
  if (!header) return { rows: [], problems: [{ line: 0, field: "batch", message: "the file has no rows" }] };

  const names = header.fields.map((h) => h.trim().toLowerCase());
  const problems: RowProblem[] = [];
  const missing = REQUIRED_COLUMNS.filter((c) => !names.includes(c));
  const unknown = names.filter((n) => n !== "" && !KNOWN.has(n));
  const repeated = names.filter((n, i) => n !== "" && names.indexOf(n) !== i);
  if (missing.length) problems.push({ line: 1, field: "columns", message: `missing column(s): ${missing.join(", ")}` });
  if (unknown.length) {
    problems.push({
      line: 1,
      field: "columns",
      message: `column(s) not accepted: ${unknown.join(", ")}. Only ${[...KNOWN].join(", ")} are stored.`,
    });
  }
  if (repeated.length) problems.push({ line: 1, field: "columns", message: `column(s) repeated: ${repeated.join(", ")}` });
  if (problems.length) return { rows: [], problems };

  const at = (fields: string[], col: string) => {
    const i = names.indexOf(col);
    return i < 0 ? "" : (fields[i] ?? "").trim();
  };
  const body = records.slice(1).filter((r) => r.fields.some((f) => f.trim() !== ""));
  if (body.length === 0) return { rows: [], problems: [{ line: 0, field: "batch", message: "the file has no rows" }] };
  if (body.length > MAX_ROWS_PER_BATCH) {
    return {
      rows: [],
      problems: [{ line: 0, field: "batch", message: `the file has ${body.length} rows; upload at most ${MAX_ROWS_PER_BATCH} at a time` }],
    };
  }

  const firstMobile = new Map<string, number>();
  const firstRef = new Map<string, number>();
  const rows: RosterRow[] = [];

  for (const rec of body) {
    const bad = (field: string, message: string) => problems.push({ line: rec.line, field, message });
    const rawMobile = at(rec.fields, "mobile");
    const row: RosterRow = {
      line: rec.line,
      mobile: rawMobile,
      full_name: at(rec.fields, "full_name"),
      category: at(rec.fields, "category"),
      member_ref: at(rec.fields, "member_ref"),
    };
    const group = at(rec.fields, "group_label");
    const location = at(rec.fields, "location_label");
    const plan = at(rec.fields, "plan_id");
    const speed = at(rec.fields, "speed_mbps");
    const days = at(rec.fields, "valid_days");
    const until = at(rec.fields, "valid_until");
    if (group) row.group_label = group;
    if (location) row.location_label = location;
    if (plan) row.plan_id = plan;
    if (speed) row.speed_mbps = speed;
    if (days) row.valid_days = days;
    if (until) row.valid_until = until;

    const mobile = normalizeMobile(rawMobile);
    if (mobile === null) bad("mobile", `"${rawMobile}" is not a 10-digit Indian mobile number`);
    else if (firstMobile.has(mobile)) bad("mobile", `mobile ${mobile} already appears on line ${firstMobile.get(mobile)}`);
    else {
      firstMobile.set(mobile, rec.line);
      row.mobile = mobile;
    }

    const nameLen = runes(row.full_name);
    if (nameLen === 0 || nameLen > MAX_NAME) bad("full_name", `name is required, at most ${MAX_NAME} characters`);

    const refLen = runes(row.member_ref);
    if (refLen === 0 || refLen > MAX_REF) bad("member_ref", `member reference is required, at most ${MAX_REF} characters`);
    else if (firstRef.has(row.member_ref)) {
      bad("member_ref", `member reference "${row.member_ref}" already appears on line ${firstRef.get(row.member_ref)}`);
    } else firstRef.set(row.member_ref, rec.line);

    const catLen = runes(row.category);
    if (catLen === 0 || catLen > MAX_CATEGORY) bad("category", `category is required, at most ${MAX_CATEGORY} characters`);

    if (runes(group) > MAX_LABEL) bad("group_label", `at most ${MAX_LABEL} characters`);
    if (runes(location) > MAX_LABEL) bad("location_label", `at most ${MAX_LABEL} characters`);

    if (plan) {
      if (speed || days || until) bad("plan_id", "a row with plan_id must leave speed_mbps, valid_days and valid_until empty");
      if (runes(plan) > MAX_PLAN_ID) bad("plan_id", `plan_id is at most ${MAX_PLAN_ID} characters`);
    } else {
      if (speed && wholeNumber(speed) === null) bad("speed_mbps", `"${speed}" is not a whole number of Mbps`);
      if (days && until) bad("valid_days", "give valid_days or valid_until, not both");
      else if (days && wholeNumber(days) === null) bad("valid_days", `"${days}" is not a whole number of days`);
      else if (until) {
        if (!isDate(until)) bad("valid_until", `"${until}" is not a date in YYYY-MM-DD form`);
        else if (until < today) bad("valid_until", `${until} is already past`);
      }
    }

    rows.push(row);
  }

  return problems.length ? { rows: [], problems } : { rows, problems };
}

/** The file's SHA-256 as lowercase hex: what settles "which file was uploaded?". */
export async function sha256Hex(bytes: ArrayBuffer | Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
